import * as React from "react";
import { useParams } from "@tanstack/react-router";
import {
  DockingProvider,
  makeTabs,
  useDragCtx,
} from "../shared/docking/docking.component.tsx";
import { DockingShell } from "../shared/docking/docking-shell.component.tsx";
import { useSidebarEditMode } from "../shared/docking/docking-edit-bar-host.tsx";
import { useModulesConfig } from "../core/services/modules-config.service.ts";
import { PatientMediaProvider } from "./patient-media.provider.tsx";

function EmptyHint() {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-sf-text-muted">
      <p className="text-[13.5px] font-medium">
        Ce module n'a pas encore de widget.
      </p>
      <p className="text-[12px]">
        Activez le mode <strong>Modifier</strong> dans le menu de gauche puis
        cliquez sur ce module pour ajouter des widgets.
      </p>
    </div>
  );
}

function UserModuleContent() {
  return (
    <>
      <ApplyEditMode />
      <ContentArea />
    </>
  );
}

function ApplyEditMode() {
  const { editMode, setEditMode, actions } = useDragCtx();
  const { sidebarEditMode } = useSidebarEditMode();
  const prevSidebarRef = React.useRef(sidebarEditMode);
  React.useEffect(() => {
    if (sidebarEditMode && !editMode) {
      setEditMode(true);
    } else if (!sidebarEditMode && prevSidebarRef.current && editMode) {
      actions.commitEdit();
    }
    prevSidebarRef.current = sidebarEditMode;
  }, [sidebarEditMode, editMode, setEditMode, actions]);
  return null;
}

function ContentArea() {
  const { root, editMode } = useDragCtx();
  const isEmpty =
    root.type === "tabs" && (!root.items || root.items.length === 0);
  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-2">
      <DockingShell />
      {isEmpty && !editMode && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <EmptyHint />
        </div>
      )}
    </div>
  );
}

export function UserModulePage() {
  const params = useParams({ strict: false }) as { userModuleId?: string };
  const userModuleId = params.userModuleId ?? "";
  const config = useModulesConfig();
  const mod = config.userModules.find((m) => m.id === userModuleId);

  const initial = React.useMemo(() => makeTabs([], 0), []);

  if (!mod) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sf-text-muted">
        Module introuvable.
      </div>
    );
  }

  return (
    <PatientMediaProvider>
      <DockingProvider
        initial={initial}
        storageKey={`ligam-docking-${userModuleId}`}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <UserModuleContent />
        </div>
      </DockingProvider>
    </PatientMediaProvider>
  );
}
